import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Search } from "lucide-react";

export interface DropdownItem {
    value: string;
    label: string;
}

interface SearchableDropdownProps {
    items: DropdownItem[];
    value?: string;
    placeholder?: string;
    onSelect: (item: DropdownItem) => void;
    className?: string;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
    items,
    value,
    placeholder = "Select...",
    onSelect,
    className = "",
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef<HTMLDivElement>(null);

    const selectedItem = items.find((i) => i.value === value);

    const filtered = items.filter((item) =>
        item.label.toLowerCase().includes(search.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    return (
        <div className={`relative w-full ${className}`} ref={ref}>
            {/* Selected box */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="w-full border rounded-xl p-3 flex justify-between items-center bg-white hover:bg-gray-50 transition"
            >
                <span className="text-gray-700">
                    {selectedItem ? selectedItem.label : placeholder}
                </span>
                <ChevronDown size={18} className="text-gray-400" />
            </button>

            {/* Dropdown */}
            {open && (
                <div className="absolute mt-2 left-0 right-0 bg-white shadow-xl rounded-xl z-50 border">
                    {/* Search input */}
                    <div className="relative p-3 border-b">
                        <Search
                            size={16}
                            className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <input
                            type="text"
                            value={search}
                            autoFocus
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search..."
                            className="w-full pl-10 pr-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-gray-200"
                        />
                    </div>

                    {/* List */}
                    <div className="max-h-56 overflow-y-auto py-1">
                        {filtered.length > 0 ? (
                            filtered.map((item) => (
                                <div
                                    key={item.value}
                                    onClick={() => {
                                        onSelect(item);
                                        setOpen(false);
                                        setSearch("");
                                    }}
                                    className={`px-4 py-2 cursor-pointer rounded-lg mx-2 mb-1 hover:bg-gray-100 ${value === item.value ? "bg-gray-50" : ""
                                        }`}
                                >
                                    {item.label}
                                </div>
                            ))
                        ) : (
                            <p className="text-center py-3 text-gray-500 text-sm">
                                No results found
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;